import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import logo from '../../assets/logo.png';
import foto from '../../assets/fotolanding.jpg';
import selloVaso from '../../assets/sello-vaso.png';
import { Estrella, Regalo, Bolsa, Buscar, Flecha } from './Iconos';
import './PuntosPublico.css';

// Cliente sin token: la consulta pública no debe enviar credenciales del sistema
const publicApi = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_FAST_FOOD_SERVICE,
    timeout: 10000,
});

// El último paso nombra el regalo configurado (p. ej. "Canjéalos por una fremonada")
const pasos = (reward) => [
    { icon: <Bolsa size={20} />, title: 'Compra', text: 'Proporciona tu cédula en caja' },
    { icon: <Estrella size={18} />, title: 'Suma', text: 'Por cada compra gana puntos' },
    {
        icon: <Regalo size={20} />,
        title: 'Disfruta',
        text: reward ? `Canjéalos por una ${reward.name.replace(/\s*gratis\s*$/i, '').toLowerCase()}` : 'Canjéalos por regalos',
    },
];

// Regalo principal: el más barato disponible (define cuántos sellos tiene la tarjeta)
const regaloPrincipal = (rewards) => {
    const ordered = [...(rewards || [])].sort((a, b) => a.points_cost - b.points_cost);
    return ordered.find((r) => r.in_stock) || ordered[0] || null;
};

const MAX_SELLOS = 20; // más sellos no caben bien en la tarjeta del celular

// Tarjeta de sellos: cada punto es un vasito; al completar la tarjeta se gana el premio
const TarjetaSellos = ({ nombre, balance, reward }) => {
    const cost = Math.min(reward?.points_cost || 10, MAX_SELLOS);
    const premios = Math.floor(balance / cost);
    const resto = balance % cost;
    // Si ya completó la tarjeta justo, se muestra llena (no vacía)
    const llenos = premios > 0 && resto === 0 ? cost : resto;

    return (
        <div className="fp-card">
            <div className="fp-card-head">
                <img src={logo} alt="frè Choco Lab" className="fp-card-logo" />
                {reward && <span className="fp-card-reward">{reward.name}</span>}
            </div>
            <div className="fp-card-stamps" style={{ backgroundImage: `url(${foto})` }}>
                <div className="fp-card-grid" style={{ '--cols': cost > 10 ? Math.ceil(cost / 2) : 5 }}>
                    {Array.from({ length: cost }).map((_, i) => {
                        const lleno = i < llenos;
                        return (
                            <span key={i} className={`fp-stamp ${lleno ? 'is-full' : ''}`}
                                style={{ animationDelay: `${150 + i * 70}ms` }}>
                                {/* Mismo PNG: a color si el punto está ganado, silueta blanca si falta */}
                                <img src={selloVaso} alt="" draggable="false" />
                            </span>
                        );
                    })}
                </div>
            </div>
            <div className="fp-card-foot">
                <div>
                    <small>Nombre:</small>
                    <strong>{nombre}</strong>
                </div>
                <div className="fp-card-right">
                    <small>Disponible:</small>
                    <strong>{premios} {premios === 1 ? 'premio' : 'premios'}</strong>
                </div>
            </div>
        </div>
    );
};

const PuntosPublico = () => {
    const [cedula, setCedula] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [vitrina, setVitrina] = useState([]);
    const inputRef = useRef(null);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'frè Choco Lab · Mis puntos';
        publicApi.get('/api/loyalty/public/rewards/')
            .then((res) => setVitrina(res.data || []))
            .catch((err) => console.error('[PuntosPublico] No se pudo cargar el regalo', err));
        return () => { document.title = previousTitle; };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const clean = cedula.replace(/\D/g, '');
        if (clean.length < 10) {
            setError('Ingresa tu cédula completa (10 dígitos).');
            return;
        }
        inputRef.current?.blur(); // cierra el teclado del celular
        setLoading(true);
        setError('');
        try {
            const res = await publicApi.get('/api/loyalty/public/lookup/', { params: { cedula: clean } });
            setResult(res.data);
        } catch (err) {
            const status = err.response?.status;
            if (status === 404) {
                setError('Aún no tienes puntos con esa cédula. Pide en caja que la registren en tu próxima compra.');
            } else if (status === 429) {
                setError('Muchas consultas seguidas. Espera un minuto e intenta de nuevo.');
            } else {
                console.error('[PuntosPublico] Error en la consulta', err);
                setError('No pudimos consultar ahora. Intenta de nuevo en unos segundos.');
            }
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setResult(null);
        setCedula('');
        setError('');
    };

    const balance = result?.points_balance ?? 0;
    const reward = useMemo(() => regaloPrincipal(result ? result.rewards : vitrina), [result, vitrina]);
    const faltan = reward ? reward.points_cost - (balance % reward.points_cost) : null;
    const premios = reward ? Math.floor(balance / reward.points_cost) : 0;

    return (
        <div className={`fp ${result ? 'has-result' : ''}`}>
            {/* ===== FOTO ===== */}
            <div className="fp-photo">
                <div className="fp-frame-wrap">
                    <div className="fp-frame" style={{ backgroundImage: `url(${foto})` }} role="img"
                        aria-label="Postres de fresa y crema de frè Choco Lab" />
                </div>
            </div>

            {/* ===== CONTENIDO ===== */}
            <div className="fp-content">
                <img src={logo} alt="frè Choco Lab" className="fp-logo" />
                <p className="fp-credit">Sistema hecho por <strong>KOA</strong></p>

                {!result ? (
                    <div className="fp-panel" key="buscar">
                        <span className="fp-eyebrow"><Estrella size={12} /> Club de puntos frè</span>
                        <h1 className="fp-title">
                            Tus antojos <br className="fp-br" />ahora <span className="fp-title-accent">suman</span>
                        </h1>
                        <p className="fp-lead">
                            Cada compra que realices te da puntos. Júntalos y cámbialos por{' '}
                            {reward ? <strong>tu {reward.name}</strong> : 'regalos dulces en caja'}.
                        </p>

                        <ol className="fp-steps">
                            {pasos(reward).map((p, i) => (
                                <li key={p.title} className="fp-step" style={{ animationDelay: `${300 + i * 120}ms` }}>
                                    <span className="fp-step-icon">{p.icon}</span>
                                    <span className="fp-step-text">
                                        <strong>{p.title}</strong>
                                        <small>{p.text}</small>
                                    </span>
                                </li>
                            ))}
                        </ol>

                        <form className="fp-search" onSubmit={handleSubmit}>
                            <label htmlFor="fp-cedula" className="fp-search-label">Consulta tus puntos</label>
                            <div className="fp-search-row">
                                <div className="fp-input-wrap">
                                    <Buscar className="fp-input-icon" size={20} />
                                    <input
                                        id="fp-cedula"
                                        ref={inputRef}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="Número de cédula"
                                        value={cedula}
                                        maxLength={13}
                                        onChange={(e) => setCedula(e.target.value.replace(/\D/g, ''))}
                                    />
                                </div>
                                <button type="submit" disabled={loading} aria-label="Ver mis puntos">
                                    {loading ? <span className="fp-spinner" /> : <><span className="fp-btn-text">Ver mis puntos</span> <Flecha size={18} /></>}
                                </button>
                            </div>
                            {error && <p className="fp-error" role="alert">{error}</p>}
                        </form>
                    </div>
                ) : (
                    <div className="fp-panel fp-result" key="resultado">
                        <h2 className="fp-hello">Hola, {result.first_name}</h2>
                        <TarjetaSellos nombre={result.first_name} balance={balance} reward={reward} />
                        <p className={`fp-card-msg ${premios > 0 ? 'is-ready' : ''}`}>
                            {premios > 0
                                ? <><Regalo size={18} /> ¡Ya puedes reclamar {premios === 1 ? 'tu premio' : `tus ${premios} premios`} en caja!</>
                                : reward
                                    ? <>Te faltan <strong>{faltan}</strong> {faltan === 1 ? 'punto' : 'puntos'} para tu <strong>{reward.name}</strong></>
                                    : 'Sigue sumando puntos con cada compra.'}
                        </p>

                        <button type="button" className="fp-again" onClick={reset}>
                            Consultar otra cédula
                        </button>
                    </div>
                )}
            </div>

        </div>
    );
};

export default PuntosPublico;
